import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CalendarDays, CheckCircle2, MapPin, Ticket, X } from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { PageIntro, Reveal, Shell } from "@/components/layout/Shell";

const FIELDS = [["name", "Full name"], ["email", "Email address"], ["phone", "Phone number"], ["organization", "Organization (optional)"]];

export const RegisterModal = ({ event, onClose }) => {
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async e => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      const res = await api("/events/register", { method: "POST", body: JSON.stringify({ ...form, event_id: event.id }) });
      setDone(res);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="admin-modal" role="dialog" data-testid="event-register-modal" onClick={onClose}>
      <motion.form
        className="admin-modal-body"
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35 }}
      >
        <div className="admin-modal-head">
          <div>
            <span className="eyebrow">Event registration</span>
            <h2 style={{ textTransform: "none" }}>{event.title}</h2>
            <div className="event-modal-meta">
              <span><CalendarDays size={14} /> {formatDate(event.date)}</span>
              {event.venue && <span><MapPin size={14} /> {event.venue}</span>}
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="event-register-close" aria-label="Close"><X size={18} /></button>
        </div>
        {done ? (
          <div className="success-message" data-testid="event-register-success">
            <CheckCircle2 size={22} style={{ marginBottom: 10 }} />
            <div>{done.message}</div>
            {done.registration_id && <small className="reg-id">Reference: {done.registration_id}</small>}
            {done.checkin_code && <Link to={`/ticket/${done.checkin_code}`} className="button button-gold" style={{ marginTop: 16 }} data-testid="event-register-ticket"><Ticket size={15} /> Open my QR ticket</Link>}
          </div>
        ) : (
          <>
            <div className="admin-modal-fields">
              {FIELDS.map(([k, l]) => (
                <label key={k}>{l}
                  <input required={k !== "organization"} type={k === "email" ? "email" : "text"} value={form[k] || ""} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`register-${k}-input`} />
                </label>
              ))}
            </div>
            {err && <div className="error-message" data-testid="event-register-error">{err}</div>}
            <div className="admin-modal-actions">
              <button type="button" className="button button-ghost dark" onClick={onClose} data-testid="event-register-cancel">Cancel</button>
              <button className="button button-gold" disabled={saving} data-testid="event-register-submit">
                {saving ? "Registering…" : "Confirm registration"} <ArrowRight size={15} />
              </button>
            </div>
          </>
        )}
      </motion.form>
    </div>
  );
};

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api("/public/events").then(d => setEvents(d.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <Shell>
      <main className="inner-page">
        <PageIntro crumb="DCEA / Events" title={<>Events & <em className="gold-text">learning</em></>} description="Make time for ideas, people and practical learning. Reserve your seat in a few clicks." />
        <div className="container event-list">
          {loading ? (
            <div className="empty-state" data-testid="events-loading">Loading…</div>
          ) : events.length ? events.map((e, i) => {
            const d = new Date(e.date);
            const closed = /closed|completed|cancelled|past/i.test(e.status || "");
            const upcoming = !closed && (/registration open/i.test(e.status || "") || d >= new Date(new Date().toDateString()));
            return (
              <Reveal key={e.id} delay={i * 0.08}>
                <article className="event-row" data-testid={`events-item-${i}`}>
                  <div className="event-row-media">
                    <img src={e.image} alt={e.title} />
                    <div className="event-date">{String(d.getDate()).padStart(2, "0")}<small>{d.toLocaleDateString("en-IN", { month: "short" })}</small></div>
                  </div>
                  <div className="event-row-body">
                    <div className="event-meta">
                      <span>{e.category}</span>
                      <b>{upcoming ? "Upcoming" : "Past event"}</b>
                    </div>
                    <h2>{e.title}</h2>
                    <p>{e.description}</p>
                    <div className="event-row-facts">
                      <span><CalendarDays size={14} /> {formatDate(e.date)}</span>
                      {e.venue && <span><MapPin size={14} /> {e.venue}</span>}
                      <span className="pill">{e.status || "Published"}</span>
                    </div>
                  </div>
                  <div className="event-row-cta">
                    {upcoming ? (
                      <button className="button button-gold" onClick={() => setActive(e)} data-testid={`event-register-${i}`}>
                        <Ticket size={15} /> Register
                      </button>
                    ) : (
                      <span className="pill" data-testid={`event-closed-${i}`}>Registration closed</span>
                    )}
                  </div>
                </article>
              </Reveal>
            );
          }) : (
            <div className="empty-state" data-testid="events-empty-state">No events scheduled right now.</div>
          )}
        </div>
      </main>
      <AnimatePresence>{active && <RegisterModal event={active} onClose={() => setActive(null)} />}</AnimatePresence>
    </Shell>
  );
}
